import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "../APIs/axios";
import { viewCredentialDocument } from "../APIs/credentialApi";

function EyeIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

export default function CredentialFilesModal({ credential, onClose }) {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState({});

  useEffect(() => {
    api
      .get(`/api/v1/documents/credential/${credential.credentialId}`)
      .then((r) => setDocs(r.data))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [credential.credentialId]);

  const handleView = async (doc) => {
    setViewing((v) => ({ ...v, [doc.documentId]: true }));
    try {
      const url = await viewCredentialDocument(doc.documentId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      alert("Unable to load document. Please try again.");
    } finally {
      setViewing((v) => ({ ...v, [doc.documentId]: false }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <p className="font-semibold text-gray-900">
              {credential.credentialName}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Attached Documents</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <p className="text-sm text-gray-400 text-center py-4">Loading...</p>
          )}

          {!loading && docs?.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No documents attached.
            </p>
          )}

          {!loading && docs?.length > 0 && (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-[1fr_120px_48px] bg-gray-50 border-b border-gray-200 px-3 py-2 text-xs font-medium text-gray-500">
                <span className="pl-6">File Name</span>
                <span>Uploaded On</span>
                <span className="text-center">View</span>
              </div>
              {docs.map((doc, i) => (
                <div
                  key={doc.documentId}
                  className={`grid grid-cols-[1fr_120px_48px] items-center px-3 py-2.5 text-sm ${
                    i < docs.length - 1 ? "border-b border-gray-100" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <svg
                      className="w-4 h-4 text-gray-400 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.8}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="text-gray-800 truncate text-xs">
                      {doc.fileName}
                    </span>
                  </div>
                  <span className="text-gray-500 text-xs">
                    {doc.uploadTime
                      ? new Date(doc.uploadTime).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "—"}
                  </span>
                  <div className="flex justify-center">
                    <button
                      disabled={viewing[doc.documentId]}
                      onClick={() => handleView(doc)}
                      className="text-green-600 hover:text-green-700 transition disabled:opacity-40"
                      title="View file"
                    >
                      <EyeIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
